import styled from 'styled-components';

import { Divider } from 'ui/divider';
import { colors, fontSizes, fontWeights } from 'ui/theme';

import { ModifiedByUser } from './ModifiedByUser';
import { SpecialSection } from '../details/Styled';
import { useTranslations } from '../../../hooks/useTranslations';
import { Asset } from '../../../types';
import { getFileTypeFromMimeType } from '../../../utils/images';
import { getFileSize } from '../../../utils/strings';

const DetailLabel = styled.div`
  font-weight: ${fontWeights.bold};
  line-height: ${fontSizes.s16};
`;

const FileDetail = styled.div<{ $hasAvatar?: boolean }>`
  align-items: center;
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  padding: ${({ theme, $hasAvatar }) => (!$hasAvatar ? `${theme.spacing.values.quarter} 0` : 0)};
`;

const FileSection = styled(SpecialSection)`
  padding: ${({ theme }) => `${theme.spacing.values.threeQuarter} ${theme.spacing.values.base}`};
  row-gap: ${({ theme }) => theme.spacing.values.half};
`;

const StyledDivider = styled(Divider)`
  background-color: ${colors.borderPrimary};
  border: none;
  height: ${({ theme }) => theme.spacing.values.single};
  margin: 0;
`;

export const testIds = {
  campaigns: 'cl-file-detail-section-campaigns',
  name: 'cl-file-detail-section-name',
  fileType: 'cl-file-detail-section-type',
  fileSize: 'cl-file-detail-section-size',
  fileDimensions: 'cl-file-detail-section-dimensions',
  createdBy: 'cl-file-detail-section-created-by',
  modifiedBy: 'cl-file-detail-section-modified-by',
};

const EMPTY_VALUE = '-';

export const FileDetails = ({
  asset,
  showName = false,
  ...otherProps
}: { asset: Asset; showName?: boolean }) => {
  const { t } = useTranslations();
  const hasCampaignCount = typeof asset.campaigns === 'number'; // 0 is valid

  return (
    <FileSection {...otherProps}>
      {hasCampaignCount && (
        <>
          <FileDetail data-test={testIds.campaigns}>
            <DetailLabel>{t('label.campaigns')}</DetailLabel>
            <div>{asset.campaigns}</div>
          </FileDetail>
          <StyledDivider />
        </>
      )}
      {showName && (
        <>
          <FileDetail data-test={testIds.name}>
            <DetailLabel>{t('label.name')}</DetailLabel>
            <div>{asset.assetName}</div>
          </FileDetail>
          <StyledDivider />
        </>
      )}

      <FileDetail data-test={testIds.fileType}>
        <DetailLabel>{t('label.fileType')}</DetailLabel>
        <div>{getFileTypeFromMimeType(asset.mimeType) || EMPTY_VALUE}</div>
      </FileDetail>
      <StyledDivider />
      <FileDetail data-test={testIds.fileSize}>
        <DetailLabel>{t('label.fileSize')}</DetailLabel>
        <div>{asset.size != null ? getFileSize(asset.size) : EMPTY_VALUE}</div>
      </FileDetail>
      <StyledDivider />
      <FileDetail data-test={testIds.fileDimensions}>
        <DetailLabel>{t('label.dimensions')}</DetailLabel>
        <div>
          {asset.width} &times; {asset.height}
          {t('abbreviation.pixel')}
        </div>
      </FileDetail>
      {asset.creatorUser && (
        <>
          <StyledDivider />
          <FileDetail data-test={testIds.createdBy} $hasAvatar={true}>
            <DetailLabel>{t('label.createdBy')}</DetailLabel>
            <div>
              <ModifiedByUser modifiedAt={asset.createdAt} modifiedBy={asset.creatorUser} />
            </div>
          </FileDetail>
        </>
      )}
      {asset.updatedByUser && (
        <>
          <StyledDivider />
          <FileDetail data-test={testIds.modifiedBy} $hasAvatar={true}>
            <DetailLabel>{t('label.modifiedBy')}</DetailLabel>
            <div>
              <ModifiedByUser modifiedAt={asset.updatedAt} modifiedBy={asset.updatedByUser} />
            </div>
          </FileDetail>
        </>
      )}
    </FileSection>
  );
};
